import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, ERROR_CODES } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { BasePortfolio } from '@ddt-web/domain';
import { select, Store } from '@ngrx/store';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import { Observable } from 'rxjs';

import { SharedEngagementsConstants } from '../../constants/shared-engagements.constants';
import { BpasEngagement } from '../../domain/bpas-engagement.model';
import * as actions from '../../store/engagements/engagements-ui.actions';
import { EngagementsState } from '../../store/engagements/engagements.reducer';
import { getActiveEngagement, getSpecifiedEngagementId, selectAllEngagements, selectEngagementsIsLoading, selectEngagementsIsUpdating, selectLatestEngagement } from '../../store/engagements/engagements.selectors';

@Injectable({
	providedIn: 'root'
})
export class BpasEngagementsStoreService {
	private allEngagements$: Observable<BpasEngagement[]>;
	private currentEngagement$: Observable<BpasEngagement>;
	private engagementIsLoading$: Observable<boolean>;
	private engagementIsUpdating$: Observable<boolean>;
	private selectedEngagementId$: Observable<any>;
	private activeEngagements$: Observable<BpasEngagement[]>;

	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		private store: Store<EngagementsState>
	) {
		this.init();
	}

	public init(): void {
		this.allEngagements$ = this.store.pipe(select(selectAllEngagements));
		this.currentEngagement$ = this.store.pipe(select(selectLatestEngagement));
		this.engagementIsLoading$ = this.store.pipe(select(selectEngagementsIsLoading));
		this.engagementIsUpdating$ = this.store.pipe(select(selectEngagementsIsUpdating));
		this.selectedEngagementId$ = this.store.pipe(select(getSpecifiedEngagementId));
		this.activeEngagements$ = this.store.pipe(select(getActiveEngagement));
	}

	public getAllEngagements$(): Observable<BpasEngagement[]> {
		return this.allEngagements$;
	}

	public selectCurrentEngagement$(): Observable<BpasEngagement> {
		return this.currentEngagement$;
	}

	public getActiveEngagements$(): Observable<BpasEngagement[]> {
		return this.activeEngagements$;
	}

	public getEngagementsIsLoading$(): Observable<boolean> {
		return this.engagementIsLoading$;
	}

	public getEngagementsIsUpdating$(): Observable<boolean> {
		return this.engagementIsUpdating$;
	}

	// TODO - should not be any but will be changed in a refactor
	public getSelectedEngagementId$(): Observable<any> {
		return this.selectedEngagementId$;
	}

	public loadEngagementsRequest(): void {
		this.store.dispatch(actions.loadEngagementsRequested());
	}

	public updateSelectedEngagementIdRequest(engagementId: string): void {
		this.store.dispatch(actions.updateSelectedEngagementIdRequested({ engagementId }));
	}

	public loadSpecificEngagementRequested(engagementId: string): void {
		this.store.dispatch(actions.loadOneEngagementRequested({ engagementId }));
	}

	public removePortfolioFromEngagementRequested(portfolioId: string, engagement: BpasEngagement): void {
		this.store.dispatch(actions.removePortfolioFromEngagementRequested({ portfolioId, engagement }));
	}

	public saveExistingEngagementRequest(engagement: BpasEngagement): void {
		this.updateEngagementRequest(SharedEngagementsConstants.UPDATE_MODE.EDIT, engagement, ERROR_CODES.PAC_ERROR_CODES.UPDATE_SAVE_ENGAGEMENT_ERROR);
	}

	public addNewEngagementRequest(engagement: BpasEngagement): void {
		this.updateEngagementRequest(SharedEngagementsConstants.UPDATE_MODE.NEW, engagement, ERROR_CODES.PAC_ERROR_CODES.UPDATE_ADD_NEW_ENGAGEMENT_ERROR);
	}

	public closeActiveEngagementsAndStartNewEngagementRequest(engagements: BpasEngagement[]): void {
		this.store.dispatch(actions.closeActiveEngagementsAndStartNewEngagementRequested({ engagements }));
	}

	private updateEngagementRequest(mode: string, engagement: BpasEngagement, errorCode: string): void {
		this.store.dispatch(actions.updateEngagementRequested({ mode, engagement, updateAnalysis: false, errorCode }));
	}

	// This is an engagement update which will happen in the background, therefore not triggering a loading action
	public updateEngagementAsyncRequested(mode: string, engagement: BpasEngagement, updateAnalysis: boolean): void {
		this.store.dispatch(actions.updateEngagementAsyncRequested({ mode, engagement, updateAnalysis }));
	}

	public savePortfolioInEngagement(engagement: BpasEngagement, portfolioToSave: BasePortfolio): void {
		const updatedEngagement: BpasEngagement = cloneDeep(engagement);
		const existingPortfolio = updatedEngagement.children.find(portfolio => portfolio.uniqueId === portfolioToSave.uniqueId);

		portfolioToSave.lastModifiedDisplayDate = Date.now().toString();

		if (!existingPortfolio) {
			portfolioToSave.portOrder = engagement.children.length + 1;
			updatedEngagement.children.push(portfolioToSave);
		} else {
			portfolioToSave.portOrder = portfolioToSave.portOrder || get(existingPortfolio, 'portOrder');
			portfolioToSave.origin = portfolioToSave.origin || get(existingPortfolio, 'origin');
			portfolioToSave.toolOrigin = portfolioToSave.toolOrigin || get(existingPortfolio, 'toolOrigin');

			const childIndex: number = updatedEngagement.children.findIndex(portfolio => portfolio.uniqueId === portfolioToSave.uniqueId);
			updatedEngagement.children.splice(childIndex, 1, portfolioToSave);
		}

		this.saveExistingEngagementRequest(updatedEngagement);
	}
}
